import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { refreshSession } from "@/lib/api/client";
import { tokenStore } from "@/lib/api/tokenStore";
import { authApi } from "./api";
import type { AuthResult, Me, Portal } from "./types";

type AuthStatus = "loading" | "authenticated" | "anonymous";

type AuthContextValue = {
  status: AuthStatus;
  me: Me | null;
  /** Stores tokens and user context after a successful sign-in. */
  completeSignIn: (result: AuthResult) => void;
  signOut: (options?: { everywhere?: boolean }) => Promise<void>;
  switchPortal: (portal: Portal) => Promise<void>;
  /** Re-reads /auth/me (e.g. after a profile update). */
  reload: () => Promise<void>;
  setUser: (user: Me["user"]) => void;
  hasPermission: (permission: string) => boolean;
  /** Set when the session ended unexpectedly (expired / revoked elsewhere). */
  sessionExpired: boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Owns the client-side session. On start-up it silently restores the session
 * from the refresh cookie (so reloading the page keeps the user signed in),
 * and it listens for unrecoverable 401s to drop back to the sign-in page.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [me, setMe] = useState<Me | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  const reset = useCallback(() => {
    tokenStore.clear();
    setMe(null);
    setStatus("anonymous");
    queryClient.clear();
  }, [queryClient]);

  useEffect(() => {
    let cancelled = false;
    refreshSession()
      .then((result) => {
        if (cancelled) return;
        setMe(result.data.me as Me);
        setStatus("authenticated");
      })
      .catch(() => {
        if (!cancelled) setStatus("anonymous");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(
    () =>
      tokenStore.onSessionExpired(() => {
        setSessionExpired(true);
        reset();
      }),
    [reset],
  );

  const completeSignIn = useCallback((result: AuthResult) => {
    tokenStore.set(result.accessToken);
    setMe(result.me);
    setSessionExpired(false);
    setStatus("authenticated");
  }, []);

  const signOut = useCallback(
    async ({ everywhere = false } = {}) => {
      try {
        await (everywhere ? authApi.logoutAll() : authApi.logout());
      } finally {
        reset();
      }
    },
    [reset],
  );

  const switchPortal = useCallback(
    async (portal: Portal) => {
      const { data } = await authApi.switchPortal(portal);
      tokenStore.set(data.accessToken);
      setMe(data.me);
      queryClient.clear();
    },
    [queryClient],
  );

  const reload = useCallback(async () => {
    const { data } = await authApi.me();
    setMe(data);
  }, []);

  const setUser = useCallback((user: Me["user"]) => setMe((current) => (current ? { ...current, user } : current)), []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      me,
      completeSignIn,
      signOut,
      switchPortal,
      reload,
      setUser,
      sessionExpired,
      hasPermission: (permission) => Boolean(me?.permissions.includes(permission)),
    }),
    [status, me, completeSignIn, signOut, switchPortal, reload, setUser, sessionExpired],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
